/*
 * Purpose: Cookie consent banner — analytics opt-in with admin link
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from '@/lib/analyticsConsent';
import { trackSessionStart } from '@/lib/siteAnalytics';
import { APP_ROUTES } from '@/lib/appRoutes';

export function CookieConsentBanner() {
  const [consent, setConsent] = useState<AnalyticsConsent>('unset');

  useEffect(() => {
    setConsent(getAnalyticsConsent());
  }, []);

  if (consent !== 'unset') return null;

  const accept = () => {
    setAnalyticsConsent('accepted');
    setConsent('accepted');
    trackSessionStart();
  };

  const decline = () => {
    setAnalyticsConsent('declined');
    setConsent('declined');
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed bottom-[calc(52px+env(safe-area-inset-bottom))] md:bottom-4 left-4 right-4 z-50 mx-auto max-w-xl rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-lg p-4 sm:p-5"
    >
      <div className="flex gap-3">
        <Cookie className="h-5 w-5 shrink-0 text-[hsl(var(--ab-green-core))] mt-0.5" />
        <div className="min-w-0 space-y-3">
          <div>
            <p className="text-sm font-medium text-foreground">Analytics cookies</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              We use optional cookies to measure site usage, flow errors, and improve Autoblocks.
              Admins can view aggregated analytics — no vault secrets are collected.{' '}
              <Link to={APP_ROUTES.help} className="underline underline-offset-2 hover:text-foreground">
                Learn more
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={accept}>
              Accept analytics
            </Button>
            <Button size="sm" variant="outline" onClick={decline}>
              Decline
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
